import { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';

import { BusEventType, eventBus } from '../../../utils/EventBus';
import { IOnChangeOptions, ISaveManagerChildrenParams, TChildren } from '../types';

import { SAVE_AFTER_STOP_DELAY } from './constants';

interface ISaveManagerExtensionProps {
  noteId: string;
  value: string;

  onSave?(contentToSave: string, targetNoteId: string): void;

  onChange(content: string): void;

  children: TChildren<ISaveManagerChildrenParams>;
}

export function SaveManagerExtension({
  noteId,
  value,
  onChange,
  onSave,
  children,
}: ISaveManagerExtensionProps) {
  const timer = useRef<NodeJS.Timer | number>(0);

  // Контент едет ВМЕСТЕ с запросом на сохранение: debounce-таймер и blur читают
  // этот ref, а не рендер-замыкания — иначе сохранялось бы значение, каким оно
  // было в момент планирования таймера (F19).
  const latestContentRef = useRef<string>(value);

  // Dirty-флаг — ref, не state: его читают beforeunload и таймер, которым нужна
  // синхронная актуальность, а не значение последнего commit'а.
  const wasChangedRef = useRef(false);

  // Чья правка накоплена: сохранение всегда адресуется этой заметке, даже если
  // проп noteId уже успел смениться (flush при переключении).
  const ownerNoteIdRef = useRef(noteId);

  // onSave потребителя — через ref, а не через deps: его зовут debounce-таймер и cleanup на
  // unmount, которым нужен handleSave ПОСЛЕДНЕГО коммита (свежий note в guard'е), а не тот,
  // что был захвачен при планировании. Заодно saveWith и вся цепочка над ним становятся
  // стабильными, и cleanup ниже срабатывает только на реальном размонтировании.
  // useLayoutEffect: между commit и пассивным эффектом колбэк уже может понадобиться.
  const onSaveRef = useRef(onSave);
  useLayoutEffect(() => {
    onSaveRef.current = onSave;
  });

  const clearOwnTimer = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current as number);
      timer.current = 0;
    }
  }, []);

  const saveWith = useCallback(
    (contentToSave: string, targetNoteId: string) => {
      wasChangedRef.current = false;
      clearOwnTimer();

      const currentOnSave = onSaveRef.current;
      if (typeof currentOnSave === 'function') {
        currentOnSave(contentToSave, targetNoteId);
      }
    },
    [clearOwnTimer],
  );

  const handleSaveNote = useCallback(() => {
    saveWith(latestContentRef.current, ownerNoteIdRef.current);
  }, [saveWith]);

  // «Конец правки» без blur: размонтирование редактора и барьер перед уходом со страницы.
  // Только при dirty — иначе затёрли бы свежепринятый внешний контент (тот же принцип,
  // что у handleBlur).
  const flushPendingSave = useCallback(() => {
    if (wasChangedRef.current) {
      saveWith(latestContentRef.current, ownerNoteIdRef.current);
    }
  }, [saveWith]);

  // Смена заметки (mount сменой не считается): несохранённая правка ПРЕДЫДУЩЕЙ
  // заметки флашится с её же id — иначе висящий таймер записал бы текст A в B,
  // а простой сброс молча терял бы правку A.
  useEffect(() => {
    if (ownerNoteIdRef.current !== noteId) {
      if (wasChangedRef.current) {
        saveWith(latestContentRef.current, ownerNoteIdRef.current);
      }

      clearOwnTimer();
      ownerNoteIdRef.current = noteId;
      latestContentRef.current = value;
      wasChangedRef.current = false;
    }
  }, [noteId, value, saveWith, clearOwnTimer]);

  // Внешнее/merge-обновление ТОЙ ЖЕ заметки приходит через value без onUpdate
  // (setContent с emitUpdate=false) — зеркалим, чтобы blur/debounce сохраняли то,
  // что пользователь реально видит, а не доисторический текст.
  useEffect(() => {
    if (ownerNoteIdRef.current === noteId) {
      latestContentRef.current = value;
    }
  }, [value, noteId]);

  const handleSaveAfterStop = useCallback(() => {
    clearOwnTimer();

    timer.current = setTimeout(() => {
      handleSaveNote();
    }, SAVE_AFTER_STOP_DELAY);
  }, [clearOwnTimer, handleSaveNote]);

  const handleChange = useCallback(
    (newContent: string, options?: IOnChangeOptions) => {
      latestContentRef.current = newContent;
      onChange(newContent);

      if (options?.saveImmediately) {
        saveWith(newContent, ownerNoteIdRef.current);
      } else {
        handleSaveAfterStop();

        wasChangedRef.current = true;
      }
    },
    [handleSaveAfterStop, onChange, saveWith],
  );

  const handleBlur = useCallback(() => {
    // Без несохранённых правок blur молчит: иначе после внешнего обновления
    // он мог бы затереть свежепринятый контент.
    if (wasChangedRef.current) {
      handleSaveNote();
    }
  }, [handleSaveNote]);

  const handleBeforeUnload = useCallback(
    (event: BeforeUnloadEvent) => {
      if (wasChangedRef.current) {
        // most modern browsers don't support custom message
        event.preventDefault();

        // some browsers support this step;
        // eslint-disable-next-line no-param-reassign
        event.returnValue = 'Wait until saving..';
        handleSaveNote();
      }
    },
    [handleSaveNote],
  );

  useEffect(() => {
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [handleBeforeUnload]);

  // Размонтирование редактора (выход из режима редактирования в ленте, уход с заметки) —
  // это тоже «конец правки». Blur здесь ненадёжен: на iOS кнопки не забирают фокус, blur
  // не приходит, а висящий debounce-таймер пережил бы компонент. Поэтому грязную правку
  // флашим в пассивном cleanup unmount'а — синхронно внутри cleanup, до любого следующего
  // ввода, а не по таймеру — и в любом случае снимаем таймер. (Для discrete-событий, вроде
  // тапа по кнопке, React 18 запускает такие cleanup'ы синхронно до paint; для default-lane
  // триггеров — отдельной задачей чуть позже, что для сохранения безразлично.)
  // Deps стабильны (flushPendingSave → saveWith → clearOwnTimer с пустыми deps), так что
  // cleanup выполняется только на реальном unmount; при StrictMode-replay wasChangedRef
  // ещё false — ничего не пишем.
  useEffect(
    () => () => {
      flushPendingSave();
      clearOwnTimer();
    },
    [flushPendingSave, clearOwnTimer],
  );

  // Барьер перед уходом со страницы: его эмитит cleanup размонтирования `EditorPage`
  // (`useLeaveEditorPage`) ДО очистки проекта, потому что после `PROJECT_CLEAN` сага
  // отбрасывает сохранение. Для самого редактора это вторая линия — он флашит и на своём
  // unmount; барьер важен тем, кто со страницей не уходит либо уходит позже (WSL-5/WSL-14).
  useEffect(() => {
    const handleFlushRequest = () => {
      flushPendingSave();
    };

    eventBus.on(BusEventType.EDITOR_FLUSH_PENDING_SAVES, handleFlushRequest);

    return () => {
      eventBus.off(BusEventType.EDITOR_FLUSH_PENDING_SAVES, handleFlushRequest);
    };
  }, [flushPendingSave]);

  const params: ISaveManagerChildrenParams = useMemo(
    () => ({
      noteId,
      value,
      onChange: handleChange,
      onBlur: handleBlur,
    }),
    [noteId, value, handleChange, handleBlur],
  );

  return children(params);
}
